import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import type {ProductCatalogItem, StoreEconomicsNotice} from '@ozon-ai/ozon/contracts';
import {TriangleAlertIcon} from 'lucide-react';
import {Alert, AlertAction, AlertDescription, AlertTitle} from '@/components/ui/alert.tsx';
import {Button} from '@/components/ui/button.tsx';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog.tsx';
import {ScrollArea} from '@/components/ui/scroll-area.tsx';
import {createPurchaseForMissingCogs} from './create-purchase-for-missing-cogs';

type StoreEconomicsNoticesProps = {
	notices: StoreEconomicsNotice[];
	skuLabels: Map<string, string>;
	periodFrom: string;
	catalog: ProductCatalogItem[];
};

function formatSkuRef(
	sku: string,
	offerId: string | null,
	skuLabels: Map<string, string>,
): string {
	const name = skuLabels.get(sku);
	if (name) {
		return offerId ? `${name} · SKU ${sku} · ${offerId}` : `${name} · SKU ${sku}`;
	}
	return offerId ? `SKU ${sku} · артикул ${offerId}` : `SKU ${sku}`;
}

function noticeBullets(notice: StoreEconomicsNotice, skuLabels: Map<string, string>): string[] {
	const bullets: string[] = [];
	if (notice.items?.length) {
		for (const item of notice.items) {
			bullets.push(formatSkuRef(item.sku, item.offerId, skuLabels));
		}
	}
	if (notice.lines?.length) {
		bullets.push(...notice.lines);
	}
	return bullets;
}

export function StoreEconomicsNotices({
	notices,
	skuLabels,
	periodFrom,
	catalog,
}: StoreEconomicsNoticesProps) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [purchaseBusy, setPurchaseBusy] = useState(false);
	const [purchaseError, setPurchaseError] = useState<string | null>(null);
	const actionableCount = useMemo(
		() => notices.filter((notice) => notice.id !== 'shipped_units').length,
		[notices],
	);

	if (notices.length === 0) {
		return null;
	}

	const preview =
		actionableCount > 0
			? `Проверьте ${actionableCount} пункт(а) — от этого зависят себестоимость, налог и сопоставление с SKU.`
			: 'Есть пояснения к метрикам расчёта.';

	return (
		<>
			<Alert variant="warning">
				<TriangleAlertIcon/>
				<AlertTitle>Замечания к расчёту</AlertTitle>
				<AlertDescription>{preview}</AlertDescription>
				<AlertAction>
					<Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
						Подробнее
					</Button>
				</AlertAction>
			</Alert>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent
					className="flex max-h-[calc(100dvh-2rem)] flex-col gap-4 overflow-hidden sm:max-w-lg"
				>
					<DialogHeader className="shrink-0">
						<DialogTitle>Замечания к расчёту</DialogTitle>
						<DialogDescription>
							Что учтено в экономике магазина и что стоит проверить в данных.
						</DialogDescription>
					</DialogHeader>
					<ScrollArea className="min-h-0 max-h-[calc(100dvh-11rem)]">
						<div className="flex flex-col gap-4 pr-3">
							{notices.map((notice) => {
								const bullets = noticeBullets(notice, skuLabels);
								return (
									<section key={notice.id} className="flex flex-col gap-2">
										<h3 className="text-sm font-medium">{notice.title}</h3>
										<p className="text-sm text-muted-foreground">{notice.summary}</p>
										{bullets.length > 0 ? (
											<ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
												{bullets.map((bullet) => (
													<li key={bullet}>{bullet}</li>
												))}
											</ul>
										) : null}
										{notice.id === 'missing_cogs' ? (
											<div className="mt-1 flex flex-col gap-2">
												<div className="flex flex-wrap gap-2">
													<Button
														type="button"
														variant="default"
														size="sm"
														disabled={purchaseBusy || !notice.items?.length}
														onClick={() => {
															if (!notice.items?.length || !periodFrom) return;
															setPurchaseError(null);
															setPurchaseBusy(true);
															void createPurchaseForMissingCogs({
																effectiveFrom: periodFrom,
																items: notice.items,
																catalog,
																skuLabels,
															})
																.then((purchaseId) => {
																	setOpen(false);
																	navigate(`/purchases/${encodeURIComponent(purchaseId)}`);
																})
																.catch((err) => {
																	setPurchaseError(
																		err instanceof Error
																			? err.message
																			: 'Не удалось создать закупку',
																	);
																})
																.finally(() => setPurchaseBusy(false));
														}}
													>
														{purchaseBusy ? 'Создаём…' : 'Создать закупку'}
													</Button>
													<Button
														type="button"
														variant="outline"
														size="sm"
														disabled={purchaseBusy}
														onClick={() => {
															setOpen(false);
															navigate('/purchases');
														}}
													>
														Перейти в закупки
													</Button>
												</div>
												{purchaseError ? (
													<p className="text-sm text-destructive">{purchaseError}</p>
												) : null}
											</div>
										) : null}
									</section>
								);
							})}
						</div>
					</ScrollArea>
				</DialogContent>
			</Dialog>
		</>
	);
}
